import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, Edit2, Trash2, Eye, EyeOff, CheckCircle, XCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface VapiAccount {
  id: string;
  keyName: string;
  friendlyName: string;
  maskedKey: string;
  decryptedValue: string;
  isValid: boolean;
  isPrimary: boolean;
}

interface VapiAccountManagerProps {
  apiKeys: any;
  onRefresh: () => void;
  user: any;
}

export const VapiAccountManager = ({ apiKeys, onRefresh, user }: VapiAccountManagerProps) => {
  const { toast } = useToast();
  const [accounts, setAccounts] = useState<VapiAccount[]>([]);
  const [editingAccount, setEditingAccount] = useState<string | null>(null);
  const [showingSecret, setShowingSecret] = useState<{[key: string]: boolean}>({});
  const [validationStatus, setValidationStatus] = useState<{[key: string]: 'idle' | 'testing' | 'valid' | 'invalid'}>({});
  const [isAddingAccount, setIsAddingAccount] = useState(false);
  
  const [editForm, setEditForm] = useState({
    friendlyName: '',
    apiKey: ''
  });

  // Load accounts from backend
  useEffect(() => {
    if (apiKeys) {
      loadAccounts();
    }
  }, [apiKeys]);

  const loadAccounts = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('manage-api-keys', {
        body: { 
          action: 'get', 
          filter_provider: 'vapi' 
        }
      });

      if (error) throw error;

      const vapiAccounts: VapiAccount[] = (data?.keys || []).map((k: any) => ({
        id: k.id,
        keyName: k.key_name,
        friendlyName: k.friendlyName || 'VAPI Account',
        maskedKey: maskApiKey(k.decrypted_value),
        decryptedValue: k.decrypted_value,
        isValid: true,
        isPrimary: k.key_name === 'api_key'
      }));

      // Sort primary first
      vapiAccounts.sort((a, b) => {
        if (a.isPrimary) return -1;
        if (b.isPrimary) return 1;
        return 0;
      });

      setAccounts(vapiAccounts);

      // Set validation status for all accounts
      const newValidationStatus: {[key: string]: 'idle' | 'testing' | 'valid' | 'invalid'} = {};
      vapiAccounts.forEach(account => {
        newValidationStatus[account.id] = 'valid';
      });
      setValidationStatus(newValidationStatus);
    } catch (error) {
      console.error('Error loading VAPI accounts:', error);
    }
  };

  const maskApiKey = (key: string): string => {
    if (!key || key.length < 8) return key;
    return key.substring(0, 4) + '•'.repeat(Math.max(0, key.length - 8)) + key.substring(key.length - 4);
  };

  const saveApiKey = async (provider: string, keyName: string, value: string, friendlyName?: string): Promise<{success: boolean; maskedSuffix?: string}> => {
    try {
      // Clear cache
      if (user?.id) {
        const cacheKey = `auth_api_keys_cache_${user.id}`;
        localStorage.removeItem(cacheKey);
      }

      const { data, error } = await supabase.functions.invoke('manage-api-keys', {
        body: {
          action: 'save',
          provider,
          key_name: keyName,
          value,
          friendly_name: friendlyName
        }
      });

      if (error) throw new Error(error.message || 'Failed to save API key');
      
      return data || { success: true };
    } catch (error) {
      console.error('Error saving API key:', error);
      return { success: false };
    }
  };

  const deleteApiKey = async (accountId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.functions.invoke('manage-api-keys', {
        body: {
          action: 'delete',
          id: accountId
        }
      });

      if (error) throw new Error(error.message || 'Failed to delete API key');
      
      return data?.success !== false;
    } catch (error) {
      console.error('Error deleting API key:', error);
      return false;
    }
  };

  const testApiKey = async (accountId: string) => {
    const account = accounts.find(a => a.id === accountId);
    if (!account) return;

    setValidationStatus(prev => ({ ...prev, [accountId]: 'testing' }));

    try {
      const response = await fetch('https://api.vapi.ai/assistant', {
        headers: {
          'Authorization': `Bearer ${account.decryptedValue}`,
          'Content-Type': 'application/json'
        }
      });

      const isValid = response.ok;
      setValidationStatus(prev => ({ ...prev, [accountId]: isValid ? 'valid' : 'invalid' }));
      
      toast({
        title: isValid ? "Test Successful" : "Test Failed",
        description: isValid ? `${account.friendlyName} is working correctly` : "API key validation failed",
        variant: isValid ? "default" : "destructive"
      });
    } catch (error) {
      setValidationStatus(prev => ({ ...prev, [accountId]: 'invalid' }));
      toast({
        title: "Test Failed",
        description: "Failed to validate API key",
        variant: "destructive"
      });
    }
  };

  const handleEdit = (accountId: string) => {
    const account = accounts.find(a => a.id === accountId);
    if (account) {
      setEditForm({
        friendlyName: account.friendlyName,
        apiKey: ''
      });
      setEditingAccount(accountId);
    }
  };

  const handleSaveEdit = async (accountId: string) => {
    const account = accounts.find(a => a.id === accountId);
    if (!account) return;

    try {
      const { data, error } = await supabase.functions.invoke('manage-api-keys', {
        body: {
          action: 'save',
          provider: 'vapi',
          key_name: account.keyName,
          value: editForm.apiKey || account.decryptedValue
        }
      });

      if (error) throw error;

      toast({
        title: "Account Updated",
        description: `${account.friendlyName} has been updated successfully`
      });
      
      setEditingAccount(null);
      setEditForm({ friendlyName: '', apiKey: '' });
      onRefresh();
      loadAccounts();
    } catch (error) {
      toast({
        title: "Update Failed",
        description: "Failed to update VAPI account",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (accountId: string) => {
    const account = accounts.find(a => a.id === accountId);
    if (!account) return;

    if (confirm(`Are you sure you want to delete ${account.friendlyName}?`)) {
      try {
        const success = await deleteApiKey(accountId);
        
        if (success) {
          toast({
            title: "Account Deleted",
            description: `${account.friendlyName} has been deleted`
          });
          onRefresh();
          loadAccounts();
        } else {
          throw new Error('Delete failed');
        }
      } catch (error) {
        toast({
          title: "Delete Failed",
          description: "Failed to delete VAPI account",
          variant: "destructive"
        });
      }
    }
  };

  const handleAddAccount = async () => {
    if (!editForm.friendlyName || !editForm.apiKey) {
      toast({
        title: "Validation Error",
        description: "Both friendly name and API key are required",
        variant: "destructive"
      });
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('manage-api-keys', {
        body: {
          action: 'save',
          provider: 'vapi',
          value: editForm.apiKey,
          friendly_name: editForm.friendlyName
        }
      });

      if (error) throw error;

      toast({
        title: "Account Added",
        description: `${editForm.friendlyName} has been added successfully`
      });
      
      setIsAddingAccount(false);
      setEditForm({ friendlyName: '', apiKey: '' });
      onRefresh();
      loadAccounts();
    } catch (error) {
      toast({
        title: "Add Failed",
        description: "Failed to add VAPI account",
        variant: "destructive"
      });
    }
  };

  const toggleSecretVisibility = (accountId: string) => {
    setShowingSecret(prev => ({ ...prev, [accountId]: !prev[accountId] }));
  };

  const getStatusIcon = (status: 'idle' | 'testing' | 'valid' | 'invalid') => {
    switch (status) {
      case 'testing':
        return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
      case 'valid':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'invalid':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: 'idle' | 'testing' | 'valid' | 'invalid') => {
    switch (status) {
      case 'testing':
        return <Badge variant="secondary">Testing...</Badge>;
      case 'valid':
        return <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">Connected</Badge>;
      case 'invalid':
        return <Badge variant="destructive">Invalid</Badge>;
      default:
        return <Badge variant="outline">Not tested</Badge>;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-base font-medium">VAPI Accounts</Label>
        <Button
          onClick={() => setIsAddingAccount(true)}
          size="sm"
          variant="outline"
          className="h-8"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Account
        </Button>
      </div>

      {accounts.length === 0 && !isAddingAccount && (
        <Card className="p-4 text-center text-muted-foreground">
          No VAPI accounts configured. Click "Add Account" to get started.
        </Card>
      )}

      {accounts.map((account) => (
        <Card key={account.id} className="p-4">
          {editingAccount === account.id ? (
            <div className="space-y-3">
              <div>
                <Label>API Key</Label>
                <Input
                  type="password"
                  value={editForm.apiKey}
                  onChange={(e) => setEditForm(prev => ({ ...prev, apiKey: e.target.value }))}
                  placeholder="Enter new API key (leave empty to keep current)"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => handleSaveEdit(account.id)}
                  size="sm"
                >
                  Save
                </Button>
                <Button
                  onClick={() => setEditingAccount(null)}
                  size="sm"
                  variant="outline"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{account.friendlyName}</span>
                  {getStatusBadge(validationStatus[account.id] || 'idle')}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-muted-foreground font-mono">
                    {showingSecret[account.id] ? account.decryptedValue : account.maskedKey}
                  </span>
                  <Button
                    onClick={() => toggleSecretVisibility(account.id)}
                    size="sm"
                    variant="ghost"
                    className="h-6 w-6 p-0"
                  >
                    {showingSecret[account.id] ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getStatusIcon(validationStatus[account.id] || 'idle')}
                <Button
                  onClick={() => testApiKey(account.id)}
                  size="sm"
                  variant="outline"
                  disabled={validationStatus[account.id] === 'testing'}
                >
                  Test
                </Button>
                <Button
                  onClick={() => handleEdit(account.id)}
                  size="sm"
                  variant="outline"
                >
                  <Edit2 className="h-4 w-4" />
                </Button>
                {!account.isPrimary && (
                  <Button
                    onClick={() => handleDelete(account.id)}
                    size="sm"
                    variant="outline"
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          )}
        </Card>
      ))}

      {isAddingAccount && (
        <Card className="p-4 border-2 border-dashed">
          <div className="space-y-3">
            <div>
              <Label>Friendly Name</Label>
              <Input
                value={editForm.friendlyName}
                onChange={(e) => setEditForm(prev => ({ ...prev, friendlyName: e.target.value }))}
                placeholder="e.g., Production Account, Test Account"
              />
            </div>
            <div>
              <Label>API Key</Label>
              <Input
                type="password"
                value={editForm.apiKey}
                onChange={(e) => setEditForm(prev => ({ ...prev, apiKey: e.target.value }))}
                placeholder="Enter VAPI API key"
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleAddAccount}
                size="sm"
              >
                Add Account
              </Button>
              <Button
                onClick={() => {
                  setIsAddingAccount(false);
                  setEditForm({ friendlyName: '', apiKey: '' });
                }}
                size="sm"
                variant="outline"
              >
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};